/**
 * PlaceCoreEventsSection — "Det händer här"
 * Horizontal scroll of upcoming events linked to producers at this place.
 * Intent-first: no price, no booking copy. Click leads to public event page.
 */
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlaceExperiences } from "@/hooks/usePlaceExperiences";
import { experienceLabel } from "@/lib/experiences";
import { CoverMediaImage } from "@/components/media/CoverMediaImage";
import type { PlaceExperience } from "@/components/place/types";
import { useTranslate } from "@/i18n";

interface Props {
  producerIds: string[];
}

/** Format time range: "HH:mm–HH:mm", "HH:mm", or null */
function formatEventTimeRange(startsAt?: string, endsAt?: string): string | null {
  if (!startsAt) return null;
  const start = new Date(startsAt);
  if (isNaN(start.getTime())) return null;
  const startTime = format(start, "HH:mm", { locale: sv });
  if (!endsAt) return startTime;
  const end = new Date(endsAt);
  if (isNaN(end.getTime())) return startTime;
  const endTime = format(end, "HH:mm", { locale: sv });
  if (endTime <= startTime) return startTime;
  return `${startTime}–${endTime}`;
}

/* ── Status helpers ─────────────────────────────── */

function isFull(event: PlaceExperience): boolean {
  if (event.maxSpots == null) return false; // unlimited
  return (event.bookedSpots ?? 0) >= event.maxSpots;
}

type DeadlineStatus = "closed" | "closing_soon" | null;

function deadlineStatus(event: PlaceExperience): DeadlineStatus {
  if (event.bookingMode === "none") return null;
  if (event.bookingDeadlineHours == null) return null;

  const start = new Date(event.startsAt);
  if (isNaN(start.getTime())) return null;

  const deadlineAt = new Date(start.getTime() - event.bookingDeadlineHours * 3600_000);
  const now = new Date();

  if (now >= deadlineAt && now < start) return "closed";

  const hoursLeft = (deadlineAt.getTime() - now.getTime()) / 3600_000;
  if (hoursLeft > 0 && hoursLeft <= 24) return "closing_soon";

  return null;
}

function EventTeaser({ event }: { event: PlaceExperience }) {
  const t = useTranslate();
  const startDate = new Date(event.startsAt);
  const dateStr = format(startDate, "d MMM", { locale: sv });
  const timeStr = formatEventTimeRange(event.startsAt, event.endsAt);
  const linkTo = event.bookingUrl || "/";
  const isDisabled = !event.bookingUrl;

  const full = isFull(event);
  const deadline = deadlineStatus(event);
  const showVenue = !event.useProducerAddress && !!event.venueText;

  return (
    <Link
      to={linkTo}
      aria-disabled={isDisabled}
      className={`flex-shrink-0 w-[260px] md:w-auto snap-start group ${isDisabled ? "pointer-events-none opacity-60" : ""}`}
    >
      <div className="rounded-xl border border-border/50 bg-card overflow-hidden transition-colors hover:border-primary/30 hover:shadow-sm h-full flex flex-col">
        <div className="aspect-[16/9] overflow-hidden">
          <CoverMediaImage
            ownerType="event"
            ownerId={event.id}
            alt={event.title}
            variant={320}
            aspect="16/9"
            className="rounded-none"
          />
        </div>
        <div className="p-3.5 flex flex-col flex-1">
          {/* Type chip + status badges */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Badge variant="secondary" className="font-normal text-xs">
              {experienceLabel(event.eventType)}
            </Badge>
            {full && (
              <Badge variant="outline" className="font-normal text-xs text-muted-foreground">
                {t("status.fullyBooked")}
              </Badge>
            )}
            {deadline === "closed" && (
              <Badge variant="outline" className="font-normal text-xs text-muted-foreground">
                {t("booking.event.closed")}
              </Badge>
            )}
            {deadline === "closing_soon" && (
              <Badge variant="outline" className="font-normal text-xs text-muted-foreground">
                Anmälan stänger snart
              </Badge>
            )}
          </div>

          <p className="font-medium text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
            {event.title}
          </p>

          <div className="flex flex-col gap-0.5 text-xs text-muted-foreground mt-auto">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {dateStr}
              </span>
              {timeStr && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {timeStr}
                </span>
              )}
            </div>
            {showVenue && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {event.venueText}
              </span>
            )}
          </div>
          {/* No price — intent-first (ActivitiesCore v1) */}
        </div>
      </div>
    </Link>
  );
}

function SkeletonCards() {
  return (
    <div className="flex gap-3 overflow-hidden">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex-shrink-0 w-[260px] rounded-xl border border-border/50 overflow-hidden">
          <Skeleton className="aspect-[16/9] w-full" />
          <div className="p-3.5 space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PlaceCoreEventsSection({ producerIds }: Props) {
  const { events, loading } = usePlaceExperiences(producerIds, 6);

  // Don't render section at all if no producers or empty + not loading
  if (!producerIds.length) return null;
  if (!loading && events.length === 0) return null;

  return (
    <section>
      <h2 className="font-display text-xl text-foreground mb-1">Det händer här</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Upptäck kurser, utflykter och aktiviteter på platsen.
      </p>

      {loading ? (
        <SkeletonCards />
      ) : (
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-hide md:grid md:grid-cols-2 lg:grid-cols-3 md:overflow-x-visible md:snap-none md:pb-0 md:mx-0 md:px-0">
          {events.map((event) => (
            <EventTeaser key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}
